import JSZip from "jszip";

// [S-266] File intake for the upload portal — turns a folder drop / folder
// pick / multi-file selection into a flat, filtered list of File objects ready
// for the per-file upload pipeline.
//
// Two sources feed this:
//   1. Drag-drop of a folder → the HTML5 entries API (webkitGetAsEntry) lets us
//      recursively walk directory trees. A plain FileList from a folder drop is
//      empty, so without this a dropped folder silently does nothing.
//   2. The <input webkitdirectory> / <input multiple> pickers → a flat FileList
//      (folders arrive already flattened, with webkitRelativePath set).
//
// Both paths converge on `filterAccepted`, which drops unsupported files and
// enforces a batch cap — and REPORTS how many were skipped/truncated so the UI
// can tell the user honestly (never a silent drop).

// Client-side batch cap. Distinct from the server's per-ZIP 100-entry guardrail:
// this bounds how many individual files we'll stage + upload one-by-one.
export const MAX_BATCH_FILES = 500;

// Hard ceiling on how many files we'll even walk out of a dropped tree before
// filtering — a runaway-folder backstop so a giant directory can't hang the tab.
const MAX_WALK_FILES = 5000;

// Extensions the ingestion pipeline accepts (mirrors the file-picker `accept`).
// `.zip` is included — the server extracts it into per-file ingests (S-267).
export const ACCEPTED_EXTENSIONS: ReadonlySet<string> = new Set([
  "docx",
  "pdf",
  "txt",
  "md",
  "markdown",
  "zip",
]);

export type IntakeResult = {
  files: File[];
  /** Count of dropped/picked files whose type isn't supported (reported, not staged). */
  skipped: number;
  /** True when the accepted set exceeded MAX_BATCH_FILES and was truncated. */
  truncated: boolean;
};

function extOf(name: string): string {
  const i = name.lastIndexOf(".");
  return i < 0 ? "" : name.slice(i + 1).toLowerCase();
}

export function isAcceptedFile(name: string): boolean {
  return ACCEPTED_EXTENSIONS.has(extOf(name));
}

/** Filter to supported files + enforce the batch cap, reporting what was dropped. */
export function filterAccepted(files: File[]): IntakeResult {
  const accepted = files.filter((f) => isAcceptedFile(f.name));
  const skipped = files.length - accepted.length;
  const truncated = accepted.length > MAX_BATCH_FILES;
  return {
    files: truncated ? accepted.slice(0, MAX_BATCH_FILES) : accepted,
    skipped,
    truncated,
  };
}

function fileFromEntry(entry: FileSystemFileEntry): Promise<File | null> {
  return new Promise((resolve) => {
    entry.file(
      (f) => resolve(f),
      () => resolve(null)
    );
  });
}

function readEntries(
  reader: FileSystemDirectoryReader
): Promise<FileSystemEntry[]> {
  return new Promise((resolve) => {
    reader.readEntries(
      (batch) => resolve(batch),
      () => resolve([])
    );
  });
}

async function walkEntry(entry: FileSystemEntry, out: File[]): Promise<void> {
  if (out.length >= MAX_WALK_FILES) return;
  if (entry.isFile) {
    const f = await fileFromEntry(entry as FileSystemFileEntry);
    if (f !== null) out.push(f);
    return;
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    // readEntries returns results in batches — loop until it returns none.
    let batch = await readEntries(reader);
    while (batch.length > 0) {
      for (const child of batch) {
        await walkEntry(child, out);
        if (out.length >= MAX_WALK_FILES) return;
      }
      batch = await readEntries(reader);
    }
  }
}

/**
 * Collect files from a drop, walking any dropped folders recursively.
 *
 * IMPORTANT: the entries API (webkitGetAsEntry) must be called synchronously
 * while the DataTransferItemList is still alive — so we capture every entry up
 * front, before the first await, then walk them.
 */
export async function collectFilesFromDataTransfer(
  dt: DataTransfer
): Promise<IntakeResult> {
  const items = dt.items;
  const entries: FileSystemEntry[] = [];
  if (items !== undefined && items.length > 0) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item === undefined) continue;
      const getEntry = (
        item as DataTransferItem & {
          webkitGetAsEntry?: () => FileSystemEntry | null;
        }
      ).webkitGetAsEntry;
      if (typeof getEntry === "function") {
        const entry = getEntry.call(item);
        if (entry !== null) entries.push(entry);
      }
    }
  }

  // No entries API (older browsers) → fall back to the flat file list.
  if (entries.length === 0) {
    return filterAccepted(Array.from(dt.files));
  }

  const collected: File[] = [];
  for (const entry of entries) {
    await walkEntry(entry, collected);
    if (collected.length >= MAX_WALK_FILES) break;
  }
  return filterAccepted(collected);
}

// ── [S-527] Client-side ZIP expansion (template bulk upload) ─────────────────
// The template flow is client-orchestrated (per-file parse → submit-draft with
// metadata), so a dropped .zip is expanded IN THE BROWSER into individual files
// fed through the existing queue — a different path from the deals portal, which
// extracts server-side (S-267). The server never sees the archive here, so these
// caps protect the user's own tab; each extracted file still passes the server's
// per-file 50MB limit + parseTemplateBytes validation.
export const ZIP_MAX_ENTRIES = 100;
export const ZIP_MAX_ENTRY_BYTES = 50 * 1024 * 1024; // 50MB per extracted file
export const ZIP_MAX_TOTAL_UNCOMPRESSED_BYTES = 200 * 1024 * 1024; // zip-bomb guard

export type ArchiveExpandResult = {
  files: File[];
  skipped: { name: string; reason: string }[];
  /** True when entry-count or total-size caps truncated the archive. */
  truncated: boolean;
};

function isZipFile(f: File): boolean {
  // A .docx is itself a PK zip, but its name ends in .docx — key off .zip only.
  return f.name.toLowerCase().endsWith(".zip");
}

function baseName(entryName: string): string {
  const parts = entryName.split(/[/\\]/);
  return parts[parts.length - 1] ?? entryName;
}

function isUnsafePath(name: string): boolean {
  return (
    name.includes("..") ||
    name.startsWith("/") ||
    name.startsWith("\\") ||
    /^[a-zA-Z]:[\\/]/.test(name)
  );
}

// jszip exposes the central-directory uncompressed size on the internal `_data`
// — not in the public typings, so read it defensively (fall back to the
// post-decompression length check when absent).
function declaredUncompressedSize(entry: JSZip.JSZipObject): number | null {
  const data = (entry as unknown as { _data?: { uncompressedSize?: number } })
    ._data;
  const size = data?.uncompressedSize;
  return typeof size === "number" && Number.isFinite(size) ? size : null;
}

/**
 * Expand any `.zip` files in `input` into their supported entries (by bare
 * extension, e.g. "docx"), passing non-zip files through untouched. Guardrails
 * reject bombs / traversal / nested archives and report what was skipped.
 */
export async function expandZipArchives(
  input: File[],
  allowedExtensions: ReadonlySet<string>
): Promise<ArchiveExpandResult> {
  const out: File[] = [];
  const skipped: { name: string; reason: string }[] = [];
  let truncated = false;

  for (const f of input) {
    if (!isZipFile(f)) {
      out.push(f);
      continue;
    }
    let zip: JSZip;
    try {
      zip = await JSZip.loadAsync(await f.arrayBuffer());
    } catch {
      skipped.push({ name: f.name, reason: "invalid zip" });
      continue;
    }

    const entries = Object.values(zip.files).filter((e) => !e.dir);
    let total = 0;
    let taken = 0;
    for (const entry of entries) {
      if (taken >= ZIP_MAX_ENTRIES) {
        truncated = true;
        break;
      }
      const rawName = entry.name;
      const name = baseName(rawName);
      if (rawName.includes("__MACOSX/") || name.startsWith("._")) continue;
      if (isUnsafePath(rawName)) {
        skipped.push({ name: rawName, reason: "unsafe path" });
        continue;
      }
      const ext = extOf(name);
      if (ext === "zip") {
        skipped.push({ name, reason: "nested archive" });
        continue;
      }
      if (!allowedExtensions.has(ext)) {
        skipped.push({ name, reason: "unsupported type" });
        continue;
      }
      const declared = declaredUncompressedSize(entry);
      if (declared !== null && declared > ZIP_MAX_ENTRY_BYTES) {
        skipped.push({ name, reason: "file too large" });
        continue;
      }
      if (
        declared !== null &&
        total + declared > ZIP_MAX_TOTAL_UNCOMPRESSED_BYTES
      ) {
        truncated = true;
        break;
      }
      const blob = await entry.async("blob");
      if (blob.size > ZIP_MAX_ENTRY_BYTES) {
        skipped.push({ name, reason: "file too large" });
        continue;
      }
      total += blob.size;
      if (total > ZIP_MAX_TOTAL_UNCOMPRESSED_BYTES) {
        truncated = true;
        break;
      }
      // No content-type needed — the server keys parsing off the filename ext.
      out.push(new File([blob], name));
      taken += 1;
    }
  }

  return { files: out, skipped, truncated };
}

/** Build the honest one-line intake summary shown after a drop/pick. */
export function describeIntake(result: IntakeResult): string {
  const { files, skipped, truncated } = result;
  if (files.length === 0) {
    return skipped > 0
      ? `No supported files found — skipped ${skipped} unsupported ${skipped === 1 ? "file" : "files"}.`
      : "No files found.";
  }
  const parts = [`Added ${files.length} ${files.length === 1 ? "file" : "files"}`];
  if (skipped > 0) parts.push(`skipped ${skipped} unsupported`);
  if (truncated) parts.push(`capped at ${MAX_BATCH_FILES}`);
  return parts.join(" · ") + ".";
}
